import { useState, type DragEvent } from "react";
import { assignNumberLabels } from "@mavatech/core";
import {
  centsFromInput,
  editableAmountFromCents,
  ITEM_CATEGORY_LABELS,
  UNIT_PRESETS,
} from "@mavatech/shared";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Package,
  Percent,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import { formatMoney } from "@mavatech/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { ArticlePicker, type PickedArticle } from "./ArticlePicker";
import { DescriptionField } from "./DescriptionField";
import { newRow, type Row } from "./types";

const MODE_OPT = [
  { value: "mandatory", label: "Fix" },
  { value: "optional", label: "Eventual" },
  { value: "alternative", label: "Alternative" },
];
const CAT_OPT = (["material", "labor", "flat"] as const).map((v) => ({
  value: v,
  label: ITEM_CATEGORY_LABELS[v],
}));

export function PositionEditor({
  rows,
  onChange,
  showModes = true,
}: {
  rows: Row[];
  onChange: (r: Row[]) => void;
  showModes?: boolean;
}) {
  // ART (84px) war zu knapp für "Alternative" (längste Option) — auf 104px verbreitert, KAT
  // dafür minimal auf 92px (siehe AUDIT-2026-10-01-UX.md, Punkt 1.6).
  const gridCols = showModes
    ? "1fr 58px 58px 84px 52px 104px 92px"
    : "1fr 58px 58px 84px 52px";
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [pickerInitialSearch, setPickerInitialSearch] = useState("");
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const favorites = trpc.article.list.useQuery({ favoritesOnly: true });

  /** Favorit per Klick als neue Position ans Ende anhängen (z. B. Anfahrtspauschale). */
  const addArticleAsPosition = (a: PickedArticle) => {
    const row = {
      ...newRow("position", null),
      description: a.name,
      unit: a.unit,
      unitPriceCents: a.salePriceCents,
      articleId: a.id,
      purchasePriceCents: a.purchasePriceCents,
    };
    onChange([...rows, row]);
  };
  const labels = assignNumberLabels(
    rows.map((r) => ({
      id: r.clientId,
      parentId: r.parentClientId,
      sortIndex: rows.indexOf(r),
      kind: r.kind,
      quantity: r.quantity,
      unitPriceCents: r.unitPriceCents,
      discountPercent: r.discountPercent,
      mode: r.mode,
      isChosenAlternative: r.isChosenAlternative,
      rangeFromLabel: r.rangeFromLabel || null,
      rangeToLabel: r.rangeToLabel || null,
    })),
  );
  // wählbare „bis"-Grenzen für Rabatt-Zeilen
  const rangeTargets = rows
    .filter((r) => r.kind === "group" || r.kind === "position")
    .map((r) => labels[r.clientId])
    .filter(Boolean);

  const patch = (clientId: string, p: Partial<Row>) =>
    onChange(rows.map((r) => (r.clientId === clientId ? { ...r, ...p } : r)));

  const descendantIds = (clientId: string): string[] => {
    const direct = rows
      .filter((r) => r.parentClientId === clientId)
      .map((r) => r.clientId);
    return [...direct, ...direct.flatMap(descendantIds)];
  };

  const remove = (clientId: string) => {
    const kill = new Set([clientId, ...descendantIds(clientId)]);
    onChange(rows.filter((r) => !kill.has(r.clientId)));
  };

  const addChild = (parentClientId: string | null, kind: Row["kind"]) => {
    const row = newRow(kind, parentClientId);
    if (parentClientId == null) {
      onChange([...rows, row]);
      return;
    }
    // hinter dem letzten Nachfahren des Parents einfügen
    const subtree = new Set([parentClientId, ...descendantIds(parentClientId)]);
    let lastIdx = rows.findIndex((r) => r.clientId === parentClientId);
    rows.forEach((r, i) => {
      if (subtree.has(r.clientId)) lastIdx = i;
    });
    onChange([...rows.slice(0, lastIdx + 1), row, ...rows.slice(lastIdx + 1)]);
  };

  const move = (clientId: string, dir: -1 | 1) => {
    const row = rows.find((r) => r.clientId === clientId);
    if (!row) return;
    const siblings = rows.filter(
      (r) => r.parentClientId === row.parentClientId,
    );
    const si = siblings.indexOf(row);
    const target = siblings[si + dir];
    if (!target) return;
    const block = (id: string) => [id, ...descendantIds(id)];
    const a = block(clientId);
    const b = block(target.clientId);
    const aRows = rows.filter((r) => a.includes(r.clientId));
    const bRows = rows.filter((r) => b.includes(r.clientId));
    const rest = rows.filter(
      (r) => !a.includes(r.clientId) && !b.includes(r.clientId),
    );
    const firstIdx = Math.min(
      rows.findIndex((r) => r.clientId === a[0]),
      rows.findIndex((r) => r.clientId === b[0]),
    );
    const reordered = dir === 1 ? [...bRows, ...aRows] : [...aRows, ...bRows];
    const out = [...rest];
    out.splice(firstIdx, 0, ...reordered);
    onChange(out);
  };

  /** Per Drag & Drop: zieht `draggedId` (samt Unterpositionen) direkt vor `targetId` — nur innerhalb derselben Ebene. */
  const dropOn = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    const dragged = rows.find((r) => r.clientId === draggedId);
    const target = rows.find((r) => r.clientId === targetId);
    if (!dragged || !target || dragged.parentClientId !== target.parentClientId)
      return;
    const block = [draggedId, ...descendantIds(draggedId)];
    const rest = rows.filter((r) => !block.includes(r.clientId));
    const blockRows = rows.filter((r) => block.includes(r.clientId));
    const targetIdx = rest.findIndex((r) => r.clientId === targetId);
    const out = [
      ...rest.slice(0, targetIdx),
      ...blockRows,
      ...rest.slice(targetIdx),
    ];
    onChange(out);
  };

  const dragHandlers = (r: Row) => ({
    onDragOver: (e: DragEvent) => {
      if (!draggingId || draggingId === r.clientId) return;
      const dragged = rows.find((x) => x.clientId === draggingId);
      // Nur gleiche Ebene (gleiches Elternelement) ist ein gültiges Ziel — sonst kein
      // preventDefault, damit der Browser den "nicht erlaubt"-Cursor zeigt statt einfach nichts
      // zu tun (das war der Hauptgrund, warum sich Drag&Drop "kaputt" angefühlt hat).
      if (!dragged || dragged.parentClientId !== r.parentClientId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragOverId !== r.clientId) setDragOverId(r.clientId);
    },
    onDragLeave: () => setDragOverId((id) => (id === r.clientId ? null : id)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      if (draggingId) dropOn(draggingId, r.clientId);
      setDraggingId(null);
      setDragOverId(null);
    },
  });

  const startDrag = (id: string) => {
    setDraggingId(id);
  };
  const endDrag = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const renderRow = (r: Row, depth: number) => {
    const indent = { paddingLeft: `${depth * 20}px` };
    if (r.kind === "group") {
      return (
        <div
          key={r.clientId}
          className={cn(
            "border border-border rounded-md bg-bg-tertiary/40 p-2 mb-2",
            draggingId === r.clientId && "opacity-40",
            dragOverId === r.clientId && "ring-2 ring-accent ring-inset",
          )}
          style={indent}
          {...dragHandlers(r)}
        >
          <div className="flex items-center gap-2">
            <RowLabel
              id={r.clientId}
              label={labels[r.clientId] ?? ""}
              onDragStart={startDrag}
              onDragEnd={endDrag}
            />
            <input
              className="flex-1 bg-bg-input border border-border rounded px-2 py-1.5 text-sm font-medium text-text-primary"
              placeholder="Gruppenbezeichnung (z. B. WC, Bad, Heizung)"
              value={r.description}
              onChange={(e) =>
                patch(r.clientId, { description: e.target.value })
              }
            />
            <RowButtons r={r} onMove={move} onRemove={remove} />
          </div>
          <div className="mt-2 flex gap-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addChild(r.clientId, "position")}
            >
              <Plus className="w-3 h-3" /> Position
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addChild(r.clientId, "text")}
            >
              <Plus className="w-3 h-3" /> Text
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addChild(r.clientId, "discount")}
            >
              <Percent className="w-3 h-3" /> Rabatt
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addChild(r.clientId, "group")}
            >
              <Plus className="w-3 h-3" /> Untergruppe
            </Button>
          </div>
        </div>
      );
    }
    if (r.kind === "text") {
      return (
        <div
          key={r.clientId}
          className={cn(
            "flex items-center gap-2 mb-2",
            draggingId === r.clientId && "opacity-40",
            dragOverId === r.clientId &&
              "ring-2 ring-accent ring-inset rounded",
          )}
          style={indent}
          {...dragHandlers(r)}
        >
          <RowLabel
            id={r.clientId}
            label=""
            onDragStart={startDrag}
            onDragEnd={endDrag}
          />
          <input
            className="flex-1 bg-bg-input border border-border rounded px-2 py-1.5 text-sm italic text-text-secondary"
            placeholder="Freitext / Hinweis"
            value={r.description}
            onChange={(e) => patch(r.clientId, { description: e.target.value })}
          />
          <RowButtons r={r} onMove={move} onRemove={remove} />
        </div>
      );
    }
    if (r.kind === "discount") {
      return (
        <div
          key={r.clientId}
          className={cn(
            "flex items-center gap-2 mb-2",
            draggingId === r.clientId && "opacity-40",
            dragOverId === r.clientId &&
              "ring-2 ring-accent ring-inset rounded",
          )}
          style={indent}
          {...dragHandlers(r)}
        >
          <RowLabel
            id={r.clientId}
            label={labels[r.clientId] ?? ""}
            onDragStart={startDrag}
            onDragEnd={endDrag}
          />
          <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary w-28">
            <Percent className="w-3.5 h-3.5 text-text-tertiary" /> Rabatt
          </span>
          <input
            type="number"
            className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-right text-text-primary w-20"
            value={r.discountPercent}
            min="0"
            max="100"
            step="0.5"
            onChange={(e) =>
              patch(r.clientId, {
                discountPercent: parseFloat(e.target.value) || 0,
              })
            }
          />
          <span className="text-xs text-text-tertiary">
            % auf Positionen von
          </span>
          <select
            className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-text-primary"
            value={r.rangeFromLabel}
            onChange={(e) =>
              patch(r.clientId, { rangeFromLabel: e.target.value })
            }
          >
            <option value="">— Anfang —</option>
            {rangeTargets.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <span className="text-xs text-text-tertiary">bis</span>
          <select
            className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-text-primary"
            value={r.rangeToLabel}
            onChange={(e) =>
              patch(r.clientId, { rangeToLabel: e.target.value })
            }
          >
            <option value="">— hier —</option>
            {rangeTargets.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <div className="flex-1" />
          <RowButtons r={r} onMove={move} onRemove={remove} />
        </div>
      );
    }
    // position
    return (
      <div
        key={r.clientId}
        className={cn(
          "mb-2",
          draggingId === r.clientId && "opacity-40",
          dragOverId === r.clientId && "ring-2 ring-accent ring-inset rounded",
        )}
        style={indent}
        {...dragHandlers(r)}
      >
        <div className="flex items-start gap-2">
          <RowLabel
            id={r.clientId}
            label={labels[r.clientId] ?? ""}
            onDragStart={startDrag}
            onDragEnd={endDrag}
            className="pt-2"
          />
          <div
            className="flex-1 grid gap-1.5"
            style={{ gridTemplateColumns: gridCols }}
          >
            <DescriptionField
              className="w-full bg-bg-input border border-border rounded px-2 py-1.5 text-sm text-text-primary"
              value={r.description}
              onChange={(text) => patch(r.clientId, { description: text })}
              onPick={(a) =>
                patch(r.clientId, {
                  articleId: a.id,
                  description: a.name,
                  unit: a.unit,
                  unitPriceCents: a.salePriceCents,
                  purchasePriceCents: a.purchasePriceCents,
                })
              }
              onExpandSearch={(search) => {
                setPickerFor(r.clientId);
                setPickerInitialSearch(search);
              }}
            />
            <input
              type="number"
              className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-right text-text-primary"
              value={r.quantity}
              step="0.5"
              min="0"
              onChange={(e) =>
                patch(r.clientId, { quantity: parseFloat(e.target.value) || 0 })
              }
            />
            <select
              className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-text-primary"
              value={r.unit}
              onChange={(e) => patch(r.clientId, { unit: e.target.value })}
              title="Einheit"
            >
              {r.unit === "" && <option value="">–</option>}
              {r.unit !== "" &&
                !UNIT_PRESETS.includes(
                  r.unit as (typeof UNIT_PRESETS)[number],
                ) && <option value={r.unit}>{r.unit}</option>}
              {UNIT_PRESETS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            <input
              inputMode="decimal"
              className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-right text-text-primary"
              defaultValue={editableAmountFromCents(r.unitPriceCents)}
              key={`${r.clientId}-p-${r.articleId ?? "x"}`}
              onChange={(e) =>
                patch(r.clientId, {
                  unitPriceCents: centsFromInput(e.target.value),
                })
              }
              placeholder="0,00"
            />
            <input
              type="number"
              className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-right text-text-primary"
              value={r.discountPercent}
              min="0"
              max="100"
              onChange={(e) =>
                patch(r.clientId, {
                  discountPercent: parseFloat(e.target.value) || 0,
                })
              }
              title="Rabatt %"
            />
            {showModes && (
              <>
                <select
                  className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-text-primary"
                  value={r.mode}
                  onChange={(e) =>
                    patch(r.clientId, { mode: e.target.value as Row["mode"] })
                  }
                  title="Fix / Eventual / Alternative"
                >
                  {MODE_OPT.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  className="bg-bg-input border border-border rounded px-1.5 py-1.5 text-sm text-text-primary"
                  value={r.itemCategory}
                  onChange={(e) =>
                    patch(r.clientId, {
                      itemCategory: e.target.value as Row["itemCategory"],
                    })
                  }
                  title="Material / Leistung / Pauschale"
                >
                  {CAT_OPT.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div className="flex items-center gap-0.5 pt-1">
            <button
              className={cn(
                "hover:text-accent",
                r.articleId ? "text-accent" : "text-text-tertiary",
              )}
              title="Artikel übernehmen"
              onClick={() => {
                setPickerFor(r.clientId);
                setPickerInitialSearch("");
              }}
            >
              <Package className="w-3.5 h-3.5" />
            </button>
            <RowButtons r={r} onMove={move} onRemove={remove} />
          </div>
        </div>
        {showModes && r.mode === "alternative" && (
          <div className="flex items-center gap-2 mt-1 ml-12 text-xs text-text-secondary">
            <span>Alternativgruppe</span>
            <input
              className="bg-bg-input border border-border rounded px-1.5 py-1 w-16 text-text-primary"
              value={r.alternativeGroup}
              placeholder="A"
              onChange={(e) =>
                patch(r.clientId, { alternativeGroup: e.target.value })
              }
            />
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={r.isChosenAlternative}
                onChange={(e) =>
                  patch(r.clientId, { isChosenAlternative: e.target.checked })
                }
              />
              als gewählt in Summe
            </label>
          </div>
        )}
      </div>
    );
  };

  const depthOf = (r: Row): number => {
    let d = 0;
    let p = r.parentClientId;
    const seen = new Set<string>();
    while (p && !seen.has(p)) {
      seen.add(p);
      d++;
      p = rows.find((x) => x.clientId === p)?.parentClientId ?? null;
    }
    return d;
  };

  return (
    <div>
      {!!favorites.data?.length && (
        <div className="mb-2">
          <button
            type="button"
            onClick={() => setFavoritesOpen((v) => !v)}
            className="flex items-center gap-1 text-2xs text-text-tertiary hover:text-accent"
            title={
              favoritesOpen ? "Favoriten einklappen" : "Favoriten anzeigen"
            }
          >
            <Star className="w-3 h-3" /> Favoriten ({favorites.data.length})
            {favoritesOpen ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
          </button>
        </div>
      )}
      {favoritesOpen && !!favorites.data?.length && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {favorites.data.map((a) => (
            <button
              key={a.id}
              type="button"
              title={`${a.name} hinzufügen (${formatMoney(a.salePriceCents)})`}
              className="px-2 py-1 rounded border border-border bg-bg-input text-2xs text-text-secondary hover:border-accent/40 hover:text-accent"
              onClick={() =>
                addArticleAsPosition({
                  id: a.id,
                  name: a.name,
                  unit: a.unit,
                  salePriceCents: a.salePriceCents,
                  purchasePriceCents: a.bestPurchasePriceCents ?? null,
                })
              }
            >
              {a.name}
            </button>
          ))}
        </div>
      )}
      {/* Spaltenkopf spiegelt die Zeile: links Nummer/Ziehgriff (w-14 + gap-2 = 64px), rechts Artikel-Knopf +
          Pfeile/Löschen (62px + gap-2 = 70px) — sonst wäre die 1fr-Spalte breiter als in den Zeilen und alle
          Beschriftungen säßen rechts neben ihrer Spalte.
          overflow-x-auto + min-width: die fixen Spalten (Menge…Kat.) brauchen zusammen mit Einrückung bei
          verschachtelten Gruppen mehr als die Beschreibungsspalte an Platz verträgt — ohne das hier würde
          "Beschreibung" auf schmaleren Bildschirmen (z. B. halbe Fensterbreite) auf wenige Pixel zusammen-
          gequetscht und der Text unsichtbar (AUDIT-2026-10-01-UX.md, bei Punkt 1.6 entdeckt). */}
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div
            className="grid gap-1.5 text-2xs font-medium text-text-tertiary uppercase tracking-wide mb-1 pl-16 pr-[70px]"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span>Beschreibung</span>
            <span className="text-right">Menge</span>
            <span>Einh.</span>
            <span className="text-right">E-Preis</span>
            <span className="text-right">Rab.%</span>
            {showModes && <span>Art</span>}
            {showModes && <span>Kat.</span>}
          </div>

          {rows.map((r) => renderRow(r, depthOf(r)))}
        </div>
      </div>

      <div
        className={cn(
          "flex gap-2 mt-2 flex-wrap",
          rows.length && "border-t border-border-subtle pt-2",
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addChild(null, "group")}
        >
          <Plus className="w-3.5 h-3.5" /> Gruppe
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addChild(null, "position")}
        >
          <Plus className="w-3.5 h-3.5" /> Position
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addChild(null, "text")}
        >
          <Plus className="w-3.5 h-3.5" /> Text
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addChild(null, "discount")}
        >
          <Percent className="w-3.5 h-3.5" /> Rabatt
        </Button>
      </div>

      <ArticlePicker
        open={pickerFor != null}
        onOpenChange={(v) => !v && setPickerFor(null)}
        initialSearch={pickerInitialSearch}
        onPick={(a) => {
          if (!pickerFor) return;
          patch(pickerFor, {
            articleId: a.id,
            description: a.name,
            unit: a.unit,
            unitPriceCents: a.salePriceCents,
            purchasePriceCents: a.purchasePriceCents,
          });
          setPickerFor(null);
        }}
      />
    </div>
  );
}

function RowButtons({
  r,
  onMove,
  onRemove,
}: {
  r: Row;
  onMove: (id: string, dir: -1 | 1) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-0.5 pt-1">
      <button
        type="button"
        className="text-text-tertiary hover:text-text-primary"
        onClick={() => onMove(r.clientId, -1)}
        title="Nach oben"
      >
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        className="text-text-tertiary hover:text-text-primary"
        onClick={() => onMove(r.clientId, 1)}
        title="Nach unten"
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        className="text-text-tertiary hover:text-danger"
        onClick={() => onRemove(r.clientId)}
        title="Löschen"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/** Nummern-Label + Ziehgriff am linken Rand jeder Zeile — bewusst hier statt rechts bei den
 * übrigen Zeilen-Aktionen, damit man ihn beim Klicken auf Package/Löschen etc. nicht aus
 * Versehen erwischt, und weil er so klarer als "Zeile an sich" statt als Zeilen-Aktion wirkt. */
function RowLabel({
  id,
  label,
  onDragStart,
  onDragEnd,
  className,
}: {
  id: string;
  label: string;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-1 w-14 shrink-0", className)}>
      <span
        className="text-text-tertiary hover:text-text-primary cursor-grab active:cursor-grabbing p-1 -m-1"
        draggable
        onDragStart={() => onDragStart(id)}
        onDragEnd={onDragEnd}
        title="Ziehen zum Verschieben"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </span>
      <span className="text-xs font-mono text-text-tertiary">{label}</span>
    </div>
  );
}
